import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthProvider.js';
import { getBranding, type BrandingConfig } from '../api/brandingApi.js';
import { getTheme, type Theme } from '../api/themesApi.js';
import {
  applyThemeToRoot,
  useThemeContext,
  type TenantThemeOverride,
  type SemanticColors,
} from '../../../shared/design-system/index.js';

/**
 * Per-tenant theme sync (multi-tenant white-label).
 *
 * Renders nothing. When a session is active it resolves the logged-in tenant's
 * white-label palette from `GET /v1/branding` (T003 branding configuration,
 * tenant derived server-side), loads the theme that branding points at
 * (`t003.theme_id` → T004) and layers both onto the SLDS base theme by
 * repainting the `:root` CSS variables. Re-applies on token / light-dark change
 * and resets to the SLDS default on sign-out. Presentation only — no business
 * logic, APIs, or data contracts are modified.
 */

/** Broadcast by the Themes screen so the repaint happens without a reload. */
export const THEME_CHANGED_EVENT = 'tenant-theme-changed';

function hex(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim() ? v.trim() : undefined;
}

/**
 * Map a T004 token bag onto the semantic palette.
 *
 * Two token vocabularies are in the table and both must paint: the flat
 * `primaryColor`/`backgroundColor`/... bag the Themes screen writes, and the
 * nested `colors.primary`/`colors.background`/... bag the M01 DEFAULT_LIGHT and
 * DEFAULT_DARK seeds use.
 */
function themeToOverride(t: Theme | null): Partial<SemanticColors> {
  if (!t?.tokens) return {};
  const flat = t.tokens as Record<string, unknown>;
  const nested = (flat.colors ?? {}) as Record<string, unknown>;
  const pick: Array<[keyof SemanticColors, unknown]> = [
    ['brandPrimary', flat.primaryColor ?? nested.primary],
    ['brandSecondary', flat.secondaryColor ?? nested.secondary],
    ['textLink', flat.accentColor ?? nested.accent],
    ['bgCanvas', flat.backgroundColor ?? nested.background],
    ['bgSurface', nested.surface],
    ['textPrimary', flat.textColor ?? nested.on_background],
    ['success', flat.successColor ?? nested.success],
    ['warning', flat.warningColor ?? nested.warning],
    ['danger', flat.dangerColor ?? nested.error],
    ['borderDefault', flat.borderColor ?? nested.border],
  ];
  const out: Record<string, string> = {};
  for (const [key, value] of pick) {
    const v = hex(value);
    if (v) out[key as string] = v;
  }
  return out as Partial<SemanticColors>;
}

/** Map the branding palette onto the semantic color override (both modes). */
function brandingToPalette(b: BrandingConfig | null): Partial<SemanticColors> {
  const palette: Record<string, string> = {};
  const primary = hex(b?.primaryColor);
  const secondary = hex(b?.secondaryColor);
  if (primary) {
    palette.brandPrimary = primary;
    palette.textLink = primary;
    palette.borderFocus = primary;
  }
  if (secondary) {
    palette.brandSecondary = secondary;
    palette.brandPrimaryHover = secondary;
  }
  return palette as Partial<SemanticColors>;
}

function toOverride(colors: Partial<SemanticColors>): TenantThemeOverride | undefined {
  if (Object.keys(colors).length === 0) return undefined;
  // The brand hue is intentionally identical across light/dark.
  return { colors: { light: colors, dark: colors } };
}

export function TenantThemeSync(): null {
  const { token } = useAuth();
  const { theme, effective } = useThemeContext();
  const [reloadKey, setReloadKey] = useState(0);

  const bump = useCallback(() => setReloadKey((n) => n + 1), []);

  useEffect(() => {
    window.addEventListener(THEME_CHANGED_EVENT, bump);
    return () => window.removeEventListener(THEME_CHANGED_EVENT, bump);
  }, [bump]);

  useEffect(() => {
    let alive = true;
    if (!token) {
      // Signed out — reset to the SLDS base.
      applyThemeToRoot(theme, effective);
      return () => {
        alive = false;
      };
    }
    void (async () => {
      let branding: BrandingConfig | null = null;
      let active: Theme | null = null;
      try {
        branding = await getBranding(token);
      } catch {
        /* branding unavailable — fall through to the SLDS base */
      }
      if (branding?.themeId) {
        try {
          active = await getTheme(branding.themeId, token);
        } catch {
          /* theme unreadable — branding colours alone still apply */
        }
      }
      if (!alive) return;
      // Branding last: its primary/secondary are the tenant's registered brand
      // and the Apply action keeps them in step with the active theme.
      applyThemeToRoot(
        theme,
        effective,
        toOverride({ ...themeToOverride(active), ...brandingToPalette(branding) }),
      );
    })();
    return () => {
      alive = false;
    };
  }, [token, theme, effective, reloadKey]);

  return null;
}
